import React, { useState } from 'react';
import { Button } from '@components/atoms/Button';
import { Badge } from '@components/atoms/Badge';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { ConnectPaymentAccountModal } from '@components/organisms/ConnectPaymentAccountModal';
import {
  useMyPaymentAccount,
  useDisconnectNmiAccount,
} from '@hooks/api/merchantPaymentAccount';
import { MerchantAccountStatus } from '@models/merchantPaymentAccount.model';
import { CreditCard } from 'lucide-react';

export const OrgPaymentAccountSection: React.FC = () => {
  const { data: paymentAccount } = useMyPaymentAccount();
  const disconnectAccount = useDisconnectNmiAccount();

  const [showConnect, setShowConnect] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const isConnected =
    !!paymentAccount &&
    paymentAccount.status === MerchantAccountStatus.CONNECTED;

  const handleRemove = async () => {
    try {
      await disconnectAccount.mutateAsync();
    } catch {
      // Error toast surfaced by the hook.
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
        Payment account
      </h4>

      {isConnected ? (
        <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400">
              <CreditCard className="w-5 h-5" />
            </div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                •••• {paymentAccount?.last4}
              </p>
              <Badge variant="success" size="sm">
                Connected
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="tertiary"
              size="md"
              onClick={() => setShowConnect(true)}>
              Change
            </Button>
            <Button
              variant="tertiary"
              size="md"
              onClick={() => setShowRemoveConfirm(true)}
              disabled={disconnectAccount.isPending}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20">
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Connect a payment account to send invoices and get paid
          </p>
          <Button
            variant="primary"
            size="md"
            onClick={() => setShowConnect(true)}>
            Connect
          </Button>
        </div>
      )}

      <ConnectPaymentAccountModal
        open={showConnect}
        onClose={() => setShowConnect(false)}
      />

      <ConfirmationModal
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={handleRemove}
        title="Remove payment account"
        description="Are you sure you want to remove this payment account? You won't be able to send invoices or get paid until you connect one again."
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default OrgPaymentAccountSection;
